import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import {
  getPlanningFinancialField,
  IJBPPlanning,
  isCanonicalPlanningRow,
  isPlanningCategoryTotal,
} from 'partner360/entities/planning'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getPaid } from '../helpers/sumActivities'
import { IMdfActivity } from '../model/types'

interface IMdfSalesScope {
  partnerId: string
  fYear: number
  programType: ProgramTypes
  actualsType: ActualsValueTypes
  timeframes: readonly string[]
  // KI editor product IDs (Category/SubCategory/L3Category), not MDF allocation labels.
  businessFocus?: readonly string[]
}

export interface IMdfContraMetrics {
  salesUsd: number | null
  contraPercent: number | null
}

export const getMdfContraPercent = (paidUsd: number, salesUsd: number | null): number | null =>
  salesUsd !== null && salesUsd > 0 ? (paidUsd / salesUsd) * 100 : null

const ALL_QUARTERS = [1, 2, 3, 4]

const getSelectedQuarters = (timeframes: readonly string[], fYear: number): number[] | null => {
  if (!timeframes.length) return ALL_QUARTERS

  const quarters = new Set<number>()
  for (const timeframe of timeframes) {
    // MDF filters use "26Q1" / "FY26 Q1"; KI Period stores bare "Q1" values.
    // Do not substitute annual sales for an unrecognized/mismatching period.
    const match = /^(?:(?:FY)?(\d{4}|\d{2})\s*)?Q([1-4])$/i.exec(timeframe.trim())
    if (!match) return null

    const year = Number(match[1])
    if (match[1] && year !== (match[1].length === 2 ? fYear % 100 : fYear)) return null
    quarters.add(Number(match[2]))
  }

  return [...quarters]
}

const isSelectedProductTotal = (row: IJBPPlanning, businessFocus: Set<string>): boolean => {
  if (!businessFocus.size || businessFocus.has(row.Category)) {
    return isPlanningCategoryTotal(row)
  }

  // A selected parent already contains its children: never add both levels.
  if (businessFocus.has(row.SubCategory) && row.SubCategory !== 'jbp-overall') {
    return !row.L3Category || row.L3Category === 'jbp-overall'
  }

  return Boolean(
    row.L3Category && row.L3Category !== 'jbp-overall' && businessFocus.has(row.L3Category)
  )
}

export const getMdfSalesUsd = (
  rows: IJBPPlanning[],
  { partnerId, fYear, programType, actualsType, timeframes, businessFocus }: IMdfSalesScope
): number | null => {
  // Contra always compares USD with USD, independently of the dashboard currency.
  // actualsType is the selected offering context's current measure, falling back
  // to the selected offering's default when empty. Once resolved, never switch
  // to another measure or LC if its USD field is absent in the planning response.
  const field = getPlanningFinancialField(actualsType, false)
  const quarters = getSelectedQuarters(timeframes, fYear)
  if (!field || !quarters) return null

  const selectedProducts = new Set(businessFocus?.map((value) => value.trim()).filter(Boolean))
  const productRows = new Map<string, IJBPPlanning[]>()
  for (const row of rows) {
    if (
      row.PartnerId !== partnerId ||
      row.FYear !== fYear ||
      !row.ProgramTypes?.split(',').some((offering) => offering.trim() === programType) ||
      !row.IsActuals ||
      row.IsTarget ||
      row.IsQuota ||
      !isCanonicalPlanningRow(row, { excludeAuxiliaryRows: true }) ||
      row.Category === 'jbp-total' ||
      !isSelectedProductTotal(row, selectedProducts) ||
      row[field] == null ||
      !Number.isFinite(Number(row[field]))
    ) {
      continue
    }

    const productKey = JSON.stringify([
      row.Category,
      row.SubCategory,
      row.L3Category || 'jbp-overall',
    ])
    const product = productRows.get(productKey) ?? []
    product.push(row)
    productRows.set(productKey, product)
  }

  let sales = 0
  let hasSales = false
  for (const product of productRows.values()) {
    // FQuarter=5 is an annual total, not an extra quarter. Never add it to Q1-Q4.
    // Fall back per product, so one focus area's annual total cannot hide another's quarters.
    const annualRows = product.filter((row) => row.FQuarter === 5)
    const selectedRows =
      quarters.length === ALL_QUARTERS.length && annualRows.length
        ? annualRows
        : product.filter((row) => quarters.includes(row.FQuarter))

    for (const row of selectedRows) {
      sales += Number(row[field])
      hasSales = true
    }
  }

  return hasSales ? sales : null
}

export const getMdfContraMetrics = (
  activities: IMdfActivity[],
  rows: IJBPPlanning[],
  scope: IMdfSalesScope
): IMdfContraMetrics => {
  // The caller supplies filtered dashboard activities or a single KI's linked list.
  // Sales use offering/period and optional KI business focus, never allocation amounts.
  const paidUsd = activities.reduce((sum, activity) => sum + getPaid(activity, true), 0)
  const salesUsd = getMdfSalesUsd(rows, scope)
  const contraPercent = getMdfContraPercent(paidUsd, salesUsd)

  return { salesUsd, contraPercent }
}
